/**
 * 🕯️ 10-27 THE SÉANCE FLICKER (Nina: "a candle flicker on screen when a séance happens", yes).
 *
 * A FRESH séance card (a resident's séance impulse: shared_content, data.command 'seance', under 15 s old) gives
 * the page one brief candle moment in the storm night's look (StormNightVeil: a dark edge vignette that leaves the
 * middle, where the chat is, readable, plus one warm candle glow low on the page). It flickers ~3.6 s, then fades.
 *  · Once per card id per page load (the hemFxFired / sceneSweepFired idiom): scrollback, reloads and a second
 *    mount of the same row never replay it.
 *  · pointer-events none: every control under it stays live. Opacity only (PERF_TODO: no blur, no box-shadow).
 *  · prefers-reduced-motion: one soft fade in and out, no flicker.
 *  · Appended to document.body, never rendered in the card: a virtualized row's transform would clip a fixed layer
 *    (fireSceneSweep's lesson).
 */
export const SEANCE_FLICKER_FRESH_MS = 15000;
export const SEANCE_FLICKER_MS = 3600;
const fired = new Set();
// 🕯️❓ 10-27 (Nina: "we need the candle light flicker and the /question command in the attic"): the séance NIGHT's
// own moments flicker too. Matched on the rows' own fields: /question's room-verb row (command 'rv_question', never
// a bare 'seance': that key belongs to the resident lane) · the séance's start card (a house_event row naming its
// template) · its twist and its wrap (House notes that carry no template, so their exact opening words, as written in
// houseEvents TEMPLATES.seance_night: the storm's "🕯️ Down to the last candle" and the read-along's lamp never match).
const SEANCE_NOTE_LEADS = ['🕯️ The candle went out on its own.', '🕯️ The seance ends.'];
/** Is this row a séance moment? Pure. */
export function isSeanceMoment(type, d) {
  if (!d) return false;
  if (!type || type === 'shared_content') {
    if (d.command === 'seance') return true; // a resident's séance impulse (the 10-27 original)
    if (d.is_house_event === true && (d.is_event_twist === true || d.is_event_end === true)) {
      const text = String(d.description || '');
      return SEANCE_NOTE_LEADS.some((lead) => text.startsWith(lead));
    }
    return false;
  }
  if (type === 'space_command') {
    if (d.command === 'rv_question') return true;
    return d.command === 'house_event' && d.event_template === 'seance_night' && d.is_event_start === true;
  }
  return false;
}

/** Should this action flicker the page now? Marks the card handled either way (an old card never fires later).
 *  Pure but for the `seen` set it is handed. */
export function seanceFlickerFor(action, nowMs = Date.now(), seen = fired) {
  if (!action || !action.id || action.isOptimistic) return false;
  const d = action.data || {};
  if (!isSeanceMoment(action.type, d)) return false;
  if (seen.has(action.id)) return false;
  seen.add(action.id);
  const at = new Date(action.created_at || d.timestamp || 0).getTime();
  if (!Number.isFinite(at) || at <= 0) return false;
  const age = nowMs - at;
  return age >= -5000 && age <= SEANCE_FLICKER_FRESH_MS; // a little clock skew is still fresh
}

const CSS_ID = 'seance-flicker-css';
const CSS = `
@keyframes seanceCandle { 0%,100% { opacity: .82; } 18% { opacity: .5; } 37% { opacity: 1; } 55% { opacity: .62; } 74% { opacity: .94; } 88% { opacity: .7; } }
@media (prefers-reduced-motion: reduce) { .seance-flicker-candle { animation: none !important; opacity: .8 !important; } }
`;

const prefersReducedMotion = () => {
  try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { return false; }
};

/** The one-shot layer. → the element, or null when there is nothing to show (no document, a hidden tab). */
export function fireSeanceFlicker({ reduced = null, doc = (typeof document !== 'undefined' ? document : null) } = {}) {
  try {
    if (!doc || !doc.body || doc.hidden) return null;
    const still = reduced === null ? prefersReducedMotion() : !!reduced;
    if (!doc.getElementById(CSS_ID)) {
      const style = doc.createElement('style');
      style.id = CSS_ID;
      style.textContent = CSS;
      (doc.head || doc.body).appendChild(style);
    }
    const veil = doc.createElement('div');
    veil.setAttribute('data-seance-flicker', still ? 'still' : 'flicker');
    veil.setAttribute('aria-hidden', 'true');
    veil.style.cssText = 'position:fixed;inset:0;z-index:9996;pointer-events:none;opacity:0;transition:opacity 700ms ease-out;'
      + 'background:radial-gradient(ellipse 70% 62% at 50% 46%, rgba(6,8,20,0.12) 0%, rgba(6,8,20,0.40) 58%, rgba(3,4,12,0.66) 100%);';
    const candle = doc.createElement('div');
    candle.className = 'seance-flicker-candle';
    candle.style.cssText = 'position:absolute;left:0;right:0;bottom:0;height:58%;pointer-events:none;'
      + 'background:radial-gradient(ellipse 46% 78% at 50% 100%, rgba(255,176,84,0.26) 0%, rgba(255,150,60,0.10) 46%, rgba(255,150,60,0) 72%);'
      + (still ? 'opacity:.8;' : 'animation:seanceCandle 1200ms ease-in-out 3;');
    veil.appendChild(candle);
    doc.body.appendChild(veil);
    const raf = (typeof window !== 'undefined' && window.requestAnimationFrame) ? window.requestAnimationFrame.bind(window) : (f) => setTimeout(f, 16);
    raf(() => { veil.style.opacity = '1'; });
    setTimeout(() => { veil.style.transition = 'opacity 1100ms ease-in'; veil.style.opacity = '0'; }, SEANCE_FLICKER_MS - 1100);
    setTimeout(() => { if (veil.parentNode) veil.parentNode.removeChild(veil); }, SEANCE_FLICKER_MS + 200);
    return veil;
  } catch (e) { return null; } // the flicker is garnish
}
